'use client'

import { useEffect, useState } from 'react'

/**
 * Resolve a stored attachment path to a viewable URL.
 *
 * In Lovable this called Supabase storage's `createSignedUrl`. v2 ships
 * without a signed-URL endpoint on the Go backend — the server returns
 * absolute URLs for now, so this hook is a near-pass-through:
 *
 *  - Absolute URLs (http/https) are returned unchanged.
 *  - Empty / nullish input returns `undefined`.
 *  - Storage paths (anything that isn't an absolute URL) are returned
 *    unchanged with a one-time dev warning, and the call site should be
 *    tagged `TODO(go-gap)` until the Go server exposes either signed URLs
 *    or fully-qualified absolute URLs everywhere.
 *
 * Once a signing endpoint exists this hook can become async + cached
 * without changing its public signature.
 */
export function useSignedAttachmentUrl(rawUrl: string | undefined): string | undefined {
  const [resolved, setResolved] = useState<string | undefined>(() =>
    isResolvedUrl(rawUrl) ? rawUrl : rawUrl
  )

  useEffect(() => {
    if (!rawUrl) {
      setResolved(undefined)
      return
    }
    if (isResolvedUrl(rawUrl)) {
      setResolved(rawUrl)
      return
    }
    // TODO(go-gap): /api/media/sign — see server/docs/discrepancies.md#media-signed-urls
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(
        '[useSignedAttachmentUrl] Got a non-absolute URL; v2 has no signed-URL endpoint yet.',
        rawUrl
      )
    }
    setResolved(rawUrl)
  }, [rawUrl])

  return resolved
}

function isResolvedUrl(value: string | undefined): boolean {
  if (!value) return false
  return value.startsWith('http://') || value.startsWith('https://') || value.startsWith('data:')
}
